import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';

// ========================
// ASYNC THUNKS
// ========================

/**
 * Obtener notificaciones del usuario
 */
export const fetchNotificaciones = createAsyncThunk(
  'notificaciones/fetchNotificaciones',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await api.get('/notificaciones', { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Error al obtener notificaciones'
      );
    }
  }
);

/**
 * Obtener cantidad de no leídas
 */
export const fetchNoLeidas = createAsyncThunk(
  'notificaciones/fetchNoLeidas',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/notificaciones/no-leidas/count');
      return response.data.count || 0;
    } catch (error) {
      return rejectWithValue(0);
    }
  }
);

/**
 * Marcar notificación como leída
 */
export const marcarComoLeida = createAsyncThunk(
  'notificaciones/marcarComoLeida',
  async (notificacionId, { rejectWithValue }) => {
    try {
      await api.put(`/notificaciones/${notificacionId}/leer`);
      return notificacionId;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Error al marcar como leída'
      );
    }
  }
);

/**
 * Marcar todas como leídas
 */
export const marcarTodasLeidas = createAsyncThunk(
  'notificaciones/marcarTodasLeidas',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.put('/notificaciones/leer-todas');
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Error al marcar todas como leídas'
      );
    }
  }
);

/**
 * Archivar notificación
 */
export const archivarNotificacion = createAsyncThunk(
  'notificaciones/archivarNotificacion',
  async (notificacionId, { rejectWithValue }) => {
    try {
      await api.delete(`/notificaciones/${notificacionId}`);
      return notificacionId;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Error al archivar notificación'
      );
    }
  }
);

/**
 * Obtener tipos de notificación (desde código + estado BD)
 */
export const fetchTiposNotificacion = createAsyncThunk(
  'notificaciones/fetchTiposNotificacion',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await api.get('/notificaciones/tipos', { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Error al obtener tipos de notificación'
      );
    }
  }
);

/**
 * Actualizar configuración de tipo (admin) - plantillas y estado
 */
export const actualizarTipoNotificacion = createAsyncThunk(
  'notificaciones/actualizarTipoNotificacion',
  async ({ codigo, datos }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/notificaciones/tipos/${codigo}`, datos);
      return { codigo, ...response.data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Error al actualizar tipo de notificación'
      );
    }
  }
);

/**
 * Restaurar plantilla a valores por defecto (admin)
 */
export const restaurarPlantilla = createAsyncThunk(
  'notificaciones/restaurarPlantilla',
  async (codigo, { rejectWithValue }) => {
    try {
      const response = await api.delete(`/notificaciones/tipos/${codigo}/plantilla`);
      return { codigo, ...response.data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Error al restaurar plantilla'
      );
    }
  }
);

/**
 * Obtener preferencias del usuario
 */
export const fetchPreferencias = createAsyncThunk(
  'notificaciones/fetchPreferencias',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/notificaciones/preferencias');
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Error al obtener preferencias'
      );
    }
  }
);

/**
 * Actualizar preferencias del usuario
 */
export const actualizarPreferencias = createAsyncThunk(
  'notificaciones/actualizarPreferencias',
  async (preferencias, { rejectWithValue }) => {
    try {
      // Formato esperado: [{ tipoCodigo, email, push }, ...]
      const response = await api.put('/notificaciones/preferencias', preferencias);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Error al actualizar preferencias'
      );
    }
  }
);

/**
 * Obtener configuración global (admin)
 */
export const fetchConfiguracionGlobal = createAsyncThunk(
  'notificaciones/fetchConfiguracionGlobal',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/notificaciones/config');
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Error al obtener configuración'
      );
    }
  }
);

/**
 * Actualizar configuración global (admin)
 */
export const actualizarConfiguracionGlobal = createAsyncThunk(
  'notificaciones/actualizarConfiguracionGlobal',
  async (configuracion, { rejectWithValue }) => {
    try {
      const response = await api.put('/notificaciones/config', configuracion);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Error al actualizar configuración'
      );
    }
  }
);

/**
 * Enviar notificación de prueba (admin)
 */
export const enviarPrueba = createAsyncThunk(
  'notificaciones/enviarPrueba',
  async ({ tipoCodigo, destinatarioId }, { rejectWithValue }) => {
    try {
      const response = await api.post('/notificaciones/test', {
        tipoCodigo,
        destinatarioId
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Error al enviar prueba'
      );
    }
  }
);

/**
 * Obtener estadísticas (admin)
 */
export const fetchEstadisticas = createAsyncThunk(
  'notificaciones/fetchEstadisticas',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await api.get('/notificaciones/estadisticas', { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Error al obtener estadísticas'
      );
    }
  }
);

// ========================
// SLICE
// ========================

const initialState = {
  // Notificaciones del usuario
  notificaciones: [],
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  },
  noLeidas: 0,
  
  // Tipos de notificación (desde código)
  tipos: [],
  tiposAgrupados: {},
  modulos: [],
  
  // Preferencias del usuario
  preferencias: [],
  preferenciasAgrupadas: {},
  
  // Configuración global (admin)
  configuracionGlobal: null,
  
  // Estadísticas (admin)
  estadisticas: null,
  
  // Estados de carga
  loading: false,
  loadingTipos: false,
  loadingPreferencias: false,
  loadingConfig: false,
  loadingEstadisticas: false,
  
  // Errores
  error: null,
  
  // UI
  filtros: {
    soloNoLeidas: false,
    modulo: null
  },
  panelAbierto: false
};

const notificacionesSlice = createSlice({
  name: 'notificaciones',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setFiltros: (state, action) => {
      state.filtros = { ...state.filtros, ...action.payload };
    },
    resetState: () => initialState,
    // Para actualización optimista
    decrementNoLeidas: (state) => {
      if (state.noLeidas > 0) {
        state.noLeidas -= 1;
      }
    },
    resetNoLeidas: (state) => {
      state.noLeidas = 0;
    },
    // Panel de notificaciones
    togglePanel: (state) => {
      state.panelAbierto = !state.panelAbierto;
    },
    abrirPanel: (state) => {
      state.panelAbierto = true;
    },
    cerrarPanel: (state) => {
      state.panelAbierto = false;
    }
  },
  extraReducers: (builder) => {
    builder
      // fetchNotificaciones
      .addCase(fetchNotificaciones.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNotificaciones.fulfilled, (state, action) => {
        state.loading = false;
        state.notificaciones = action.payload.data || [];
        state.pagination = action.payload.pagination || initialState.pagination;
      })
      .addCase(fetchNotificaciones.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // fetchNoLeidas
      .addCase(fetchNoLeidas.fulfilled, (state, action) => {
        state.noLeidas = action.payload;
      })
      
      // marcarComoLeida
      .addCase(marcarComoLeida.fulfilled, (state, action) => {
        const notificacion = state.notificaciones.find(n => n.id === action.payload);
        if (notificacion && !notificacion.leida) {
          notificacion.leida = true;
          notificacion.fechaLeida = new Date().toISOString();
          state.noLeidas = Math.max(0, state.noLeidas - 1);
        }
      })
      
      // marcarTodasLeidas
      .addCase(marcarTodasLeidas.fulfilled, (state) => {
        state.notificaciones.forEach(n => {
          n.leida = true;
          n.fechaLeida = new Date().toISOString();
        });
        state.noLeidas = 0;
      })
      
      // archivarNotificacion
      .addCase(archivarNotificacion.fulfilled, (state, action) => {
        const index = state.notificaciones.findIndex(n => n.id === action.payload);
        if (index !== -1) {
          const notificacion = state.notificaciones[index];
          if (!notificacion.leida) {
            state.noLeidas = Math.max(0, state.noLeidas - 1);
          }
          state.notificaciones.splice(index, 1);
        }
      })
      
      // fetchTiposNotificacion
      .addCase(fetchTiposNotificacion.pending, (state) => {
        state.loadingTipos = true;
      })
      .addCase(fetchTiposNotificacion.fulfilled, (state, action) => {
        state.loadingTipos = false;
        state.tipos = action.payload.tipos || [];
        state.tiposAgrupados = action.payload.agrupados || {};
        state.modulos = action.payload.modulos || [];
      })
      .addCase(fetchTiposNotificacion.rejected, (state, action) => {
        state.loadingTipos = false;
        state.error = action.payload;
      })
      
      // actualizarTipoNotificacion
      .addCase(actualizarTipoNotificacion.fulfilled, (state, action) => {
        const index = state.tipos.findIndex(t => t.codigo === action.payload.codigo);
        if (index !== -1) {
          state.tipos[index] = { ...state.tipos[index], ...action.payload };
        }
      })
      
      // fetchPreferencias
      .addCase(fetchPreferencias.pending, (state) => {
        state.loadingPreferencias = true;
      })
      .addCase(fetchPreferencias.fulfilled, (state, action) => {
        state.loadingPreferencias = false;
        state.preferencias = action.payload.preferencias || [];
        state.preferenciasAgrupadas = action.payload.agrupados || {};
      })
      .addCase(fetchPreferencias.rejected, (state, action) => {
        state.loadingPreferencias = false;
        state.error = action.payload;
      })
      
      // actualizarPreferencias
      .addCase(actualizarPreferencias.fulfilled, (state, action) => {
        // Actualizar preferencias localmente si es necesario
        if (action.payload.notificaciones) {
          // Actualizar cada preferencia modificada
          Object.entries(action.payload.notificaciones).forEach(([codigo, pref]) => {
            const idx = state.preferencias.findIndex(p => p.codigo === codigo);
            if (idx !== -1) {
              state.preferencias[idx].preferencias = pref;
            }
          });
        }
      })
      
      // fetchConfiguracionGlobal
      .addCase(fetchConfiguracionGlobal.pending, (state) => {
        state.loadingConfig = true;
      })
      .addCase(fetchConfiguracionGlobal.fulfilled, (state, action) => {
        state.loadingConfig = false;
        state.configuracionGlobal = action.payload;
      })
      .addCase(fetchConfiguracionGlobal.rejected, (state, action) => {
        state.loadingConfig = false;
        state.error = action.payload;
      })

      // actualizarConfiguracionGlobal
      .addCase(actualizarConfiguracionGlobal.fulfilled, (state, action) => {
        if (action.payload?.config) {
          state.configuracionGlobal = {
            ...(state.configuracionGlobal || {}),
            ...action.payload.config,
            // Preservar el emailConfig enmascarado del GET (no exponer apiKey cruda del PUT).
            emailConfig: state.configuracionGlobal?.emailConfig ?? action.payload.config.emailConfig,
          };
        }
      })

      // fetchEstadisticas
      .addCase(fetchEstadisticas.pending, (state) => {
        state.loadingEstadisticas = true;
      })
      .addCase(fetchEstadisticas.fulfilled, (state, action) => {
        state.loadingEstadisticas = false;
        state.estadisticas = action.payload;
      })
      .addCase(fetchEstadisticas.rejected, (state, action) => {
        state.loadingEstadisticas = false;
        state.error = action.payload;
      });
  }
});

export const { 
  clearError, 
  setFiltros, 
  resetState,
  decrementNoLeidas,
  resetNoLeidas,
  togglePanel,
  abrirPanel,
  cerrarPanel
} = notificacionesSlice.actions;

// Alias para compatibilidad
export const eliminarNotificacion = archivarNotificacion;

export default notificacionesSlice.reducer;

// ========================
// SELECTORES
// ========================

export const selectNotificaciones = (state) => state.notificaciones.notificaciones;
export const selectNoLeidas = (state) => state.notificaciones.noLeidas;
export const selectPagination = (state) => state.notificaciones.pagination;
export const selectLoading = (state) => state.notificaciones.loading;
export const selectError = (state) => state.notificaciones.error;
export const selectTipos = (state) => state.notificaciones.tipos;
export const selectTiposAgrupados = (state) => state.notificaciones.tiposAgrupados;
export const selectModulos = (state) => state.notificaciones.modulos;
export const selectPreferencias = (state) => state.notificaciones.preferencias;
export const selectPreferenciasAgrupadas = (state) => state.notificaciones.preferenciasAgrupadas;
export const selectConfiguracionGlobal = (state) => state.notificaciones.configuracionGlobal;
export const selectEstadisticas = (state) => state.notificaciones.estadisticas;
export const selectFiltros = (state) => state.notificaciones.filtros;

// Alias para compatibilidad
export const selectTiposNotificacion = selectTipos;
export const selectTiposLoading = (state) => state.notificaciones.loadingTipos;
export const selectPreferenciasLoading = (state) => state.notificaciones.loadingPreferencias;
